import { Record, List } from 'immutable';
import { minBy, find } from 'lodash';

import { intersectModelTrimsBitFieldValue } from 'commons-vehicle/ModelYear/services/DataUtilitiesService/DataUtilitiesService.js';
import VEHICLE from 'commons-vehicle/constants/vehicle.js';
import YearModel from 'commons-vehicle/ModelYear/models/YearModel.js';
import VehicleModel from 'commons-vehicle/Vehicle/models/VehicleModel.js';
import * as PurchaseMethods from 'commons-vehicle/PurchaseMethods/constants/purchaseMethods.js';
/* eslint no-bitwise: 0 */

const ModelYearRecord = Record({
    year: 0,
    make: '',
    model: '',
    bodyStyleCode: '',
    isSingleConfiguration: false,
    hasOptions: true,
    cabSizes: [],
    boxSizes: [],
    modelTrims: List([]),
    drivePowerTrains: List([]),
    optionsPackages: List([]),
    years: List([new YearModel()]),
});

class ModelYear extends ModelYearRecord {
    getAvailableModelTrims() {
        return this.modelTrims.filter((modelTrim) => modelTrim.visible);
    }

    modelYearsAreLoaded() {
        const selectedYear = this.getSelectedYear();
        return selectedYear && selectedYear.year !== 0;
    }

    modelYearIsLoaded() {
        const selectedYear = this.getSelectedYear();
        const selectedYearAsTrimsAndPowers = selectedYear && selectedYear.modelTrims.size > 0;

        return selectedYearAsTrimsAndPowers && selectedYear.year === this.year;
    }

    getSelectedYear() {
        return this.years.filter((year) => year.selected).first();
    }

    getSelectedYearPurchaseMethods() {
        const selectedYear = this.getSelectedYear();
        return selectedYear ? selectedYear.purchaseMethods : undefined;
    }

    getSelectedCabSize() {
        return find(this.cabSizes, (cabSize) => {
            return cabSize.selected;
        });
    }

    getSelectedBoxSize() {
        return find(this.boxSizes, (boxSize) => {
            return boxSize.selected;
        });
    }

    getSelectedModelTrim() {
        return this.getAvailableModelTrims()
            .filter((modelTrim) => modelTrim.selected)
            .first();
    }

    getAvailableDriveTrains() {
        return this.drivePowerTrains.filter((drivePowerTrain) => drivePowerTrain.visible);
    }

    getSelectedDrivePowerTrain() {
        return this.getAvailableDriveTrains()
            .filter((drivePowerTrain) => drivePowerTrain.selected)
            .first();
    }

    getAvailableOptionsPackages() {
        return this.optionsPackages.filter((optionsPackage) => optionsPackage.visible);
    }

    getSelectedOptionsPackage() {
        return this.getAvailableOptionsPackages()
            .filter((optionsPackage) => optionsPackage.selected)
            .first();
    }

    getSelectedOptionsPackageConfiguration() {
        const selectedOptionsPackage = this.getAvailableOptionsPackages()
            .filter((optionsPackage) => optionsPackage.selected)
            .first();
        return selectedOptionsPackage ? selectedOptionsPackage.configuration : undefined;
    }

    getOptionsPackageConfigurationFromCode(optionsPackageCode) {
        const selectedOptionsPackage = this.optionsPackages
            .filter((optionsPackage) => optionsPackage.selectionCode === optionsPackageCode)
            .first();

        return selectedOptionsPackage ? selectedOptionsPackage.configuration : undefined;
    }

    getOptionsPackageFromConfigurationId(configurationId) {
        return this.optionsPackages
            .filter((optionsPackage) => optionsPackage.configuration.id === configurationId)
            .first();
    }

    getBestAvailableOptionsPackageFromTrimsBitField(trimsBitField, engineSelectionCode, transmissionSelectionCode) {
        const availableOptionsPackages = this.optionsPackages.filter(
            (optionsPackage) =>
                intersectModelTrimsBitFieldValue(optionsPackage, trimsBitField, false) > 0 &&
                optionsPackage.configuration.engine.selectionCode === engineSelectionCode &&
                optionsPackage.configuration.transmission.selectionCode === transmissionSelectionCode
        );

        if (availableOptionsPackages.size === 0) {
            return '';
        }
        return availableOptionsPackages.minBy((optionsPackage) => optionsPackage.rank).selectionCode;
    }

    getSelectedOptionsPackageExteriorColour() {
        const configuration = this.getSelectedOptionsPackageConfiguration();
        return configuration ? configuration.getSelectedExteriorColour() : '';
    }

    getSelectedOptionsPackageInteriorColour() {
        const configuration = this.getSelectedOptionsPackageConfiguration();
        return configuration ? configuration.getSelectedInteriorColour() : '';
    }

    getSelectedOptionsPackageBestAvailableExteriorColour() {
        const configuration = this.getSelectedOptionsPackageConfiguration();
        return configuration ? configuration.exteriorColours.minBy((colour) => colour.retail) : '';
    }

    getSelectedOptionsPackagePurchaseMethods() {
        const selectedOptionsPackage = this.getAvailableOptionsPackages()
            .filter((optionsPackage) => optionsPackage.selected)
            .first();
        return selectedOptionsPackage ? selectedOptionsPackage.configuration.purchaseMethods : undefined;
    }

    getSelectedPurchaseMethods() {
        if (this.modelTrims.size > 0) {
            return this.getSelectedOptionsPackagePurchaseMethods();
        } else if (this.modelYearsAreLoaded()) {
            return this.getSelectedYearPurchaseMethods();
        }
        return '';
    }

    getPaymentCalculationWithTerm(term) {
        const purchaseMethods = this.getSelectedPurchaseMethods();
        return purchaseMethods ? purchaseMethods.getPaymentCalculationWithTerm(term) : '';
    }

    getPaymentCalculationWithPaymentFrequency(paymentFrequency) {
        const purchaseMethods = this.getSelectedPurchaseMethods();
        return purchaseMethods ? purchaseMethods.getPaymentCalculationWithPaymentFrequency(paymentFrequency) : '';
    }

    getPaymentCalculationWithCashDown(cashDown) {
        const purchaseMethods = this.getSelectedPurchaseMethods();
        return purchaseMethods ? purchaseMethods.getPaymentCalculationWithCashDown(cashDown) : '';
    }

    getPaymentCalculationWithTradeIn(tradeIn) {
        const purchaseMethods = this.getSelectedPurchaseMethods();
        return purchaseMethods ? purchaseMethods.getPaymentCalculationWithTradeIn(tradeIn) : '';
    }

    getPaymentCalculationWithLien(lien) {
        const purchaseMethods = this.getSelectedPurchaseMethods();
        return purchaseMethods ? purchaseMethods.getPaymentCalculationWithLien(lien) : '';
    }

    getPaymentCalculationWithExteriorColour(exteriorColourAmounts) {
        const purchaseMethods = this.getSelectedPurchaseMethods();
        return purchaseMethods ? purchaseMethods.getPaymentCalculationWithExteriorColour(exteriorColourAmounts) : '';
    }

    getPaymentCalculationWithInteriorColour(interiorColourAmounts) {
        const purchaseMethods = this.getSelectedPurchaseMethods();
        return purchaseMethods ? purchaseMethods.getPaymentCalculationWithInteriorColour(interiorColourAmounts) : '';
    }

    getPaymentCalculationWithOptions(optionsAmounts) {
        const purchaseMethods = this.getSelectedPurchaseMethods();
        return purchaseMethods ? purchaseMethods.getPaymentCalculationWithOptions(optionsAmounts) : '';
    }

    getPaymentCalculationWithTaxesIncluded(taxesIncluded) {
        const purchaseMethods = this.getSelectedPurchaseMethods();
        return purchaseMethods ? purchaseMethods.getPaymentCalculationWithTaxesIncluded(taxesIncluded) : '';
    }

    getSelectedExteriorColour() {
        const configurationExteriorColour = this.getSelectedOptionsPackageExteriorColour();
        const configurationExteriorColourPhoto = configurationExteriorColour ? configurationExteriorColour.photo1 : '';
        const yearExteriorColour = this.getSelectedYear();
        const yearExteriorColourPhoto = yearExteriorColour ? yearExteriorColour.exteriorColours.photo1 : '';
        return configurationExteriorColourPhoto || yearExteriorColourPhoto;
    }

    getBestPurchaseMethodYear(purchaseMethod) {
        let availableYears;
        let bestYear;
        switch (purchaseMethod) {
            case PurchaseMethods.CASH_PURCHASE:
                availableYears = this.years.toArray();
                bestYear = minBy(availableYears, (year) => year.purchaseMethods.cashPurchase.getRetail());
                break;

            case PurchaseMethods.LEASE:
                availableYears = this.years
                    .filter((year) => year.purchaseMethods.lease.getCalculatedPayment() > 0)
                    .toArray();
                bestYear = minBy(availableYears, (year) => year.purchaseMethods.lease.getCalculatedPayment());
                break;

            case PurchaseMethods.FINANCE:
                availableYears = this.years
                    .filter((year) => year.purchaseMethods.finance.getCalculatedPayment() > 0)
                    .toArray();
                bestYear = minBy(availableYears, (year) => year.purchaseMethods.finance.getCalculatedPayment());
                break;

            default:
                return 0;
        }

        return bestYear ? bestYear.year : 0;
    }

    getBestPurchaseMethodsYear() {
        return {
            cashPurchaseYear: this.getBestPurchaseMethodYear(PurchaseMethods.CASH_PURCHASE),
            leaseYear: this.getBestPurchaseMethodYear(PurchaseMethods.LEASE),
            financeYear: this.getBestPurchaseMethodYear(PurchaseMethods.FINANCE),
        };
    }

    getVehicleFromConfiguration(vehicle) {
        const veh = vehicle && vehicle instanceof VehicleModel ? vehicle : new VehicleModel();
        const configuration = this.getSelectedOptionsPackageConfiguration();

        return veh
            .set('vehicleConditionType', VEHICLE.CONDITIONS.NEW)
            .set('year', this.year)
            .set('make', this.make)
            .set('model', this.model)
            .set('bodyStyleCode', this.bodyStyleCode)
            .set('modelTrimCode', configuration.modelTrimCode)
            .set('configurationId', configuration.id)
            .set('catalogVehicleId', configuration.smNewCarId)
            .set('configuration', configuration)
            .set('purchaseMethods', configuration.purchaseMethods);
    }

    // Was made for specifics cases like `Mazda mazda3` [SHOWROOM-2152]
    modelContainsMake() {
        return this.model.includes(this.make);
    }
}

export default ModelYear;
